// Settings Panel - AgentBheem ERP
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useThemeStore, type ThemeMode } from '@/stores/themeStore'
import {
  Settings,
  User,
  Bell,
  Shield,
  Globe,
  HelpCircle,
  LogOut,
  ChevronRight,
  X,
  Moon,
  Sun,
  Monitor,
} from 'lucide-react'

interface SettingsPanelProps {
  isOpen: boolean
  onClose: () => void
}

interface SettingItemProps {
  icon: React.ReactNode
  label: string
  description?: string
  onClick?: () => void
  rightElement?: React.ReactNode
}

function SettingItem({ icon, label, description, onClick, rightElement }: SettingItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded',
        'text-left transition-all duration-200',
        'hover:bg-[var(--color-bg-card-hover)]',
        'group'
      )}
    >
      <div className="flex-shrink-0 w-10 h-10 rounded bg-[var(--color-bg-input)] flex items-center justify-center text-[var(--color-text-muted)] group-hover:text-emerald-500 transition-colors">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--color-text-primary)]">{label}</p>
        {description && <p className="text-xs text-[var(--color-text-muted)] truncate">{description}</p>}
      </div>
      {rightElement || <ChevronRight className="h-4 w-4 text-[var(--color-text-muted)] group-hover:text-[var(--color-text-secondary)]" />}
    </button>
  )
}

export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { mode, setMode } = useThemeStore()

  const handleLogout = () => {
    logout()
    onClose()
    navigate('/auth/login')
  }

  const handleNavigate = (path: string) => {
    onClose()
    navigate(path)
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />

      {/* Panel */}
      <div
        className={cn(
          'fixed right-4 top-[78px] w-80 max-h-[calc(100vh-100px)] overflow-y-auto',
          'bg-[var(--color-bg-dropdown)] border border-[var(--color-border-hover)] rounded-xl',
          'shadow-2xl shadow-black/50',
          'z-[9999]',
          'animate-fade-in-up'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-emerald-500" />
            <h3 className="font-semibold text-[var(--color-text-primary)]">Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-bg-card-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                {user?.full_name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[var(--color-text-primary)] truncate">
                {user?.full_name || user?.username || 'User'}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">{user?.email}</p>
              {user?.role && (
                <p className="text-xs text-emerald-500 capitalize mt-0.5">
                  {String(user.role).replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Theme Selector */}
        <div className="p-4 border-b border-[var(--color-border)]">
          <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider mb-3">
            Appearance
          </p>
          <div className="flex gap-2">
            {[
              { themeMode: 'light' as ThemeMode, icon: Sun, label: 'Light' },
              { themeMode: 'dark' as ThemeMode, icon: Moon, label: 'Dark' },
              { themeMode: 'system' as ThemeMode, icon: Monitor, label: 'System' },
            ].map(({ themeMode, icon: Icon, label }) => (
              <button
                key={themeMode}
                onClick={() => setMode(themeMode)}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1.5 p-3 rounded transition-all',
                  mode === themeMode
                    ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/30'
                    : 'bg-[var(--color-bg-input)] text-[var(--color-text-muted)] border border-transparent hover:bg-[var(--color-bg-card-hover)]'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Settings Menu */}
        <div className="p-2">
          <SettingItem
            icon={<User className="h-5 w-5" />}
            label="My Profile"
            description="Manage your account"
            onClick={() => handleNavigate('/dashboard/profile')}
          />
          <SettingItem
            icon={<Bell className="h-5 w-5" />}
            label="Notifications"
            description="Configure alerts"
            onClick={() => handleNavigate('/dashboard/settings/notifications')}
          />
          <SettingItem
            icon={<Shield className="h-5 w-5" />}
            label="Security"
            description="Password & 2FA"
            onClick={() => handleNavigate('/dashboard/settings/security')}
          />
          <SettingItem
            icon={<Globe className="h-5 w-5" />}
            label="Language"
            description="English (US)"
            onClick={() => handleNavigate('/dashboard/settings/language')}
          />
          <SettingItem
            icon={<HelpCircle className="h-5 w-5" />}
            label="Help & Support"
            onClick={() => handleNavigate('/dashboard/help')}
          />
        </div>

        {/* Logout */}
        <div className="p-2 border-t border-[var(--color-border)]">
          <button
            onClick={handleLogout}
            className={cn(
              'w-full flex items-center gap-3 p-3 rounded',
              'text-red-500 transition-all duration-200',
              'hover:bg-red-500/10'
            )}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded bg-red-500/10 flex items-center justify-center">
              <LogOut className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium">Sign out</span>
          </button>
        </div>

        {/* Version */}
        <div className="px-4 py-3 border-t border-[var(--color-border)]">
          <p className="text-[10px] text-[var(--color-text-muted)] text-center">
            AgentBheem ERP v1.0.0 &bull; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </>
  )
}
